import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
    ShieldCheck,
    Loader2,
    Save,
    RotateCcw,
    AlertTriangle,
    Eye,
    Rocket,
    Mail,
    KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_LABELS } from "../lib/documents";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";

const GoLiveCard = () => {
    const [dlg, setDlg] = useState(null); // {type, data}
    const [busy, setBusy] = useState(false);
    const [sending, setSending] = useState(false);
    const [savingTpl, setSavingTpl] = useState(false);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");

    const URLS = {
        welcome: "/employees/welcome-emails",
        onboarding: "/employees/onboarding-credentials-emails",
    };

    const openDlg = async (type) => {
        setBusy(true);
        try {
            const { data } = await api.post(URLS[type], { dry_run: true });
            setSubject(data.template?.subject || "");
            setBody(data.template?.body || "");
            setDlg({ type, data });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    const saveTemplate = async () => {
        if (!subject.trim() || !body.trim()) {
            toast.error("Oggetto e testo non possono essere vuoti");
            return false;
        }
        setSavingTpl(true);
        try {
            await api.put("/employees/golive-email-templates", { type: dlg.type, subject, body });
            return true;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio testo");
            return false;
        } finally {
            setSavingTpl(false);
        }
    };

    const handleSaveOnly = async () => {
        if (await saveTemplate()) toast.success("Testo email salvato");
    };

    const [sendingTest, setSendingTest] = useState(false);
    const sendTestEmail = async () => {
        if (!subject.trim() || !body.trim()) {
            toast.error("Oggetto e testo non possono essere vuoti");
            return;
        }
        setSendingTest(true);
        try {
            const { data } = await api.post("/employees/golive-email-test", { type: dlg.type, subject, body });
            toast.success(`Anteprima inviata a ${data.to} — controlla la casella`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio anteprima");
        } finally {
            setSendingTest(false);
        }
    };

    const confirm = async () => {
        if (!(await saveTemplate())) return;
        setSending(true);
        try {
            const { data } = await api.post(URLS[dlg.type], { dry_run: false });
            if (!data.emails_enabled) {
                toast.warning(`Email disattivate: nessun invio (${data.targets} destinatari pronti). Riattivale e riprova.`);
            } else {
                toast.success(`Invio avviato: ${data.queued} email in coda — dettagli nel Registro Email`);
            }
            setDlg(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio");
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="golive-card">
            <h2 className="mb-1 flex items-center gap-2 font-display text-base font-semibold text-slate-700">
                <Rocket className="h-4 w-4 text-[#376DB3]" />
                Lancio nuovo portale
            </h2>
            <p className="mb-4 text-xs text-slate-500">
                Le due email da inviare ai dipendenti dopo il go-live su hr.splendorsud.com.
                Puoi vedere e modificare il testo prima dell'invio.
            </p>
            <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => openDlg("welcome")} disabled={busy} className="gap-2" data-testid="golive-welcome-btn">
                    <Mail className="h-4 w-4" />
                    1. Benvenuto (chi ha già le credenziali)
                </Button>
                <Button variant="outline" onClick={() => openDlg("onboarding")} disabled={busy} className="gap-2" data-testid="golive-onboarding-btn">
                    <KeyRound className="h-4 w-4" />
                    2. Invio credenziali (chi non le ha)
                </Button>
            </div>

            <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
                <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden" data-testid="golive-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-slate-800">
                            {dlg?.type === "welcome" ? "Email di benvenuto" : "Invio credenziali"}
                        </DialogTitle>
                        <DialogDescription>
                            {dlg?.type === "welcome" ? (
                                <>Verrà inviata a <b>{dlg?.data.targets}</b> dipendenti con credenziali attive: presentazione del nuovo portale.</>
                            ) : (
                                <>Verranno <b>generate le credenziali standard</b> (utente = email o cognome, password = Nome+matricola) e inviate a <b>{dlg?.data.targets}</b> dipendenti senza accesso.
                                {dlg?.data.no_email > 0 && <> Altri <b>{dlg?.data.no_email}</b> non hanno email
                                {dlg?.data.no_email_list?.length > 0 && (
                                    <> (<span data-testid="golive-no-email-names">{dlg.data.no_email_list.map((e) => `${e.nome}${e.matricola ? ` — matr. ${e.matricola}` : ""}`).join("; ")}</span>)</>
                                )}: per loro usa "Genera credenziali standard" nella scheda e consegnale a mano.</>}</>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                        <div>
                            <Label htmlFor="golive-subject" className="text-xs font-semibold text-slate-600">Oggetto</Label>
                            <Input
                                id="golive-subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                className="mt-1"
                                data-testid="golive-subject-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="golive-body" className="text-xs font-semibold text-slate-600">Testo dell'email</Label>
                            <Textarea
                                id="golive-body"
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                rows={12}
                                className="mt-1 font-mono text-xs leading-relaxed"
                                data-testid="golive-body-textarea"
                            />
                            <p className="mt-1.5 text-[11px] text-slate-500">
                                Segnaposto disponibili: <code className="rounded bg-slate-100 px-1">{"{nome}"}</code> nome del dipendente,{" "}
                                <code className="rounded bg-slate-100 px-1">{"{link}"}</code> link al portale
                                {dlg?.type === "onboarding" && (
                                    <>, <code className="rounded bg-slate-100 px-1">{"{utente}"}</code> e{" "}
                                    <code className="rounded bg-slate-100 px-1">{"{password}"}</code> credenziali generate</>
                                )}. Verranno sostituiti automaticamente per ogni dipendente.
                            </p>
                        </div>
                        {!dlg?.data.emails_enabled && (
                            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="golive-emails-off-warning">
                                ⚠️ Le email sono attualmente disattivate (EMAIL_ENABLED=false): l'invio non partirà finché non vengono riattivate.
                            </p>
                        )}
                    </div>

                    <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-3">
                        <Button
                            variant="outline"
                            onClick={sendTestEmail}
                            disabled={sendingTest}
                            className="mr-auto gap-2 border-[#376DB3]/30 text-[#376DB3] hover:bg-[#376DB3]/5"
                            data-testid="golive-test-email-btn"
                        >
                            {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                            Inviami un'anteprima
                        </Button>
                        <Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button>
                        <Button
                            variant="outline"
                            onClick={handleSaveOnly}
                            disabled={savingTpl}
                            className="gap-2"
                            data-testid="golive-save-template-btn"
                        >
                            {savingTpl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Salva testo
                        </Button>
                        <Button
                            onClick={confirm}
                            disabled={sending || savingTpl || dlg?.data.targets === 0}
                            className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]"
                            data-testid="golive-confirm-btn"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            Invia a {dlg?.data.targets} dipendenti
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
};

export default function DeliverySettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [text, setText] = useState("");
    const [ackText, setAckText] = useState("");
    const [ackCategories, setAckCategories] = useState([]);
    const [version, setVersion] = useState("");
    const [updatedAt, setUpdatedAt] = useState(null);
    const [bumpVersion, setBumpVersion] = useState(false);
    const [original, setOriginal] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/delivery/settings");
            setText(data.text || "");
            setAckText(data.ack_text || "");
            setAckCategories(data.ack_categories || []);
            setVersion(data.version || "1.0");
            setUpdatedAt(data.updated_at);
            setOriginal({
                text: data.text || "",
                ackText: data.ack_text || "",
                ackCategories: [...(data.ack_categories || [])],
            });
        } catch (e) {
            toast.error("Errore caricamento impostazioni");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const dirty = original && (
        text !== original.text ||
        ackText !== original.ackText ||
        ackCategories.length !== original.ackCategories.length ||
        ackCategories.some((c) => !original.ackCategories.includes(c))
    );

    const toggleCategory = (cat) => {
        setAckCategories((prev) =>
            prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
        );
    };

    const handleReset = () => {
        if (!original) return;
        setText(original.text);
        setAckText(original.ackText);
        setAckCategories([...original.ackCategories]);
        setBumpVersion(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/delivery/settings", {
                text,
                ack_text: ackText,
                ack_categories: ackCategories,
                bump_version: bumpVersion,
            });
            setVersion(data.version);
            setUpdatedAt(data.updated_at);
            setOriginal({ text, ackText, ackCategories: [...ackCategories] });
            setBumpVersion(false);
            if (bumpVersion) {
                toast.success(`Impostazioni salvate · versione bumpata a ${data.version} — tutti i dipendenti dovranno riaccettare${data.realigned_documents ? ` · ${data.realigned_documents} documenti esistenti aggiornati` : ""}`);
            } else {
                toast.success(`Impostazioni salvate${data.realigned_documents ? ` · tolta la presa visione a ${data.realigned_documents} documenti esistenti delle categorie deselezionate` : ""}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="delivery-settings-page">
            <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <ShieldCheck className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-[#376DB3]">Impostazioni — Consegna telematica</h1>
                        <p className="text-sm text-slate-600">
                            Testo informativa, presa visione e categorie obbligatorie.
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="space-y-6">
                        <GoLiveCard />

                        {/* Version banner */}
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-white">Versione attuale {version}</Badge>
                                {updatedAt && (
                                    <span className="text-xs text-slate-500">
                                        Ultima modifica: {new Date(updatedAt).toLocaleString("it-IT")}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Informativa */}
                        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="mb-3 flex items-center justify-between">
                                <div>
                                    <Label className="text-base font-semibold text-slate-900">Informativa di consegna telematica</Label>
                                    <p className="text-xs text-slate-500">
                                        Mostrata al dipendente al primo accesso al portale. Deve essere accettata per accedere ai documenti.
                                    </p>
                                </div>
                            </div>
                            <Textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                rows={12}
                                className="font-mono text-sm leading-relaxed"
                                data-testid="settings-informativa-text"
                                placeholder="Inserisci il testo dell'informativa…"
                            />
                            <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                                <Eye className="h-3 w-3" />
                                Anteprima: i \n vengono interpretati come ritorni a capo (whitespace-pre-wrap).
                            </div>
                        </section>

                        {/* Presa visione */}
                        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="mb-3">
                                <Label className="text-base font-semibold text-slate-900">Testo presa visione</Label>
                                <p className="text-xs text-slate-500">
                                    Mostrato al dipendente prima del download di documenti che richiedono presa visione.
                                    Il placeholder <code className="rounded bg-slate-100 px-1 font-mono text-[11px]">{"{filename}"}</code> verrà sostituito con il nome del file.
                                </p>
                            </div>
                            <Textarea
                                value={ackText}
                                onChange={(e) => setAckText(e.target.value)}
                                rows={6}
                                className="font-mono text-sm leading-relaxed"
                                data-testid="settings-ack-text"
                                placeholder="Confermo di aver preso visione del documento {filename}…"
                            />
                        </section>

                        {/* Categorie */}
                        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="mb-3 flex items-start justify-between gap-3">
                                <div>
                                    <Label className="text-base font-semibold text-slate-900">Categorie con presa visione obbligatoria</Label>
                                    <p className="text-xs text-slate-500">
                                        I nuovi documenti caricati in queste categorie nasceranno con il flag attivo.
                                        Al salvataggio, i documenti esistenti non ancora firmati delle categorie deselezionate perdono il flag.
                                        L'admin può comunque togliere/aggiungere il flag manualmente su ogni documento.
                                    </p>
                                </div>
                                <Badge className="flex-shrink-0 gap-1 border-emerald-200 bg-emerald-50 text-emerald-700" variant="outline" data-testid="ack-summary-badge">
                                    <ShieldCheck className="h-3 w-3" />
                                    {ackCategories.length} attiv{ackCategories.length === 1 ? "a" : "e"}
                                </Badge>
                            </div>
                            {ackCategories.length > 0 && (
                                <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-xs text-emerald-800" data-testid="ack-active-list">
                                    <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="font-medium">Presa visione obbligatoria per:</span>
                                    {ackCategories.map((c, i) => (
                                        <span key={c} className="font-semibold">
                                            {CATEGORY_LABELS[c] || c}{i < ackCategories.length - 1 ? "," : ""}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="settings-ack-categories">
                                {Object.entries(CATEGORY_LABELS).map(([value, label]) => {
                                    const checked = ackCategories.includes(value);
                                    return (
                                        <label
                                            key={value}
                                            className={`relative flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-all ${
                                                checked
                                                    ? "border-emerald-400 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 shadow-sm"
                                                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                            }`}
                                            data-testid={`settings-ack-cat-${value}`}
                                        >
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={() => toggleCategory(value)}
                                                className={checked ? "border-emerald-500 data-[state=checked]:bg-emerald-500" : ""}
                                            />
                                            <span className="flex-1">{label}</span>
                                            {checked && (
                                                <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" data-testid={`ack-active-icon-${value}`} />
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </section>

                        {/* Force re-accept */}
                        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <label className="flex items-start gap-3 text-sm cursor-pointer">
                                <Checkbox
                                    checked={bumpVersion}
                                    onCheckedChange={(v) => setBumpVersion(!!v)}
                                    className="mt-0.5"
                                    data-testid="settings-bump-version"
                                />
                                <div>
                                    <div className="flex items-center gap-2 font-medium text-amber-900">
                                        <AlertTriangle className="h-4 w-4" />
                                        Forza riaccettazione informativa
                                    </div>
                                    <div className="text-xs text-amber-700">
                                        Incrementa il numero di versione (es. {version} → {(() => {
                                            try {
                                                const [maj, min] = version.split(".");
                                                return `${maj}.${parseInt(min) + 1}`;
                                            } catch { return "2.0"; }
                                        })()}). Tutti i dipendenti dovranno accettare nuovamente l'informativa al prossimo accesso.
                                        Usalo solo se hai modificato sostanzialmente il testo.
                                    </div>
                                </div>
                            </label>
                        </section>

                        {/* Actions */}
                        <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                            <Button
                                variant="outline"
                                onClick={handleReset}
                                disabled={!dirty || saving}
                                className="gap-2"
                                data-testid="settings-reset-btn"
                            >
                                <RotateCcw className="h-4 w-4" />
                                Annulla modifiche
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={!dirty || saving}
                                className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="settings-save-btn"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                Salva impostazioni
                            </Button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
